import { createElement, useCallback, useState } from 'react';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import { useExtendPotSubmitDeadline } from './useExtendPotSubmitDeadline';

interface PendingExtension {
  potId: string;
  weekNumber: number;
}

/**
 * Owns the extend-submit-deadline confirmation dialog, its copy, and the
 * mutation call — shared between the pot approvals list and the pot details
 * page so neither surface holds its own copy of the dialog state.
 */
export function useExtendDeadlineAction() {
  const [pending, setPending] = useState<PendingExtension | null>(null);
  const { mutate, isPending } = useExtendPotSubmitDeadline();

  const open = useCallback((potId: string, weekNumber: number) => {
    setPending({ potId, weekNumber });
  }, []);

  const handleCancel = useCallback(() => {
    setPending(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!pending) {
      return;
    }
    mutate({ potId: pending.potId }, { onSuccess: () => setPending(null) });
  }, [pending, mutate]);

  const dialog = pending
    ? createElement(ConfirmDialog, {
        open: true,
        title: 'Extend submit deadline',
        message: `This gives the owner more time to submit Week ${pending.weekNumber}. The pot stays in Week ${pending.weekNumber} and does not roll over to the current week.`,
        confirmText: 'Extend deadline',
        onConfirm: handleConfirm,
        onCancel: handleCancel,
        'data-testid': 'extend-deadline-dialog',
      })
    : null;

  return { open, dialog, isPending };
}
